import axios from "axios";

/**
	* @typedef {"one_time" | "weekly" | "monthly" | "yearly"} VacationRepeatType
	*/

/**
	* @typedef {Object} VacationView
	* @property {string} id - Unique identifier
	* @property {string} voicemailId - Voicemail ID reference
	* @property {string} title - Vacation title
	* @property {boolean} isActive - Is vacation active
	* @property {boolean} isLegacy - Legacy (v1) vacation flag
	* @property {string} message - SMS auto-reply message
	* @property {VacationRepeatType} repeatType - Vacation recurrence type
	* @property {Date} start - Start date of the vacation
	* @property {Date} end - End date of the vacation
	* @property {boolean} [addSignature] - Whether to append a signature
	*/

/**
	* @typedef {Object} VacationCreateDto
	* @property {string} voicemailId
	* @property {string} title
	* @property {boolean} isActive
	* @property {VacationRepeatType} repeatType
	* @property {string} message
	* @property {Date} start
	* @property {Date} end
	* @property {boolean} [addSignature]
	*/

/**
	* @typedef {Object} VacationUpdateDto
	* @property {string} id
	* @property {string} [voicemailId]
	* @property {string} [title]
	* @property {boolean} [isActive]
	* @property {VacationRepeatType} [repeatType]
	* @property {string} [message]
	* @property {Date} [start]
	* @property {Date} [end]
	* @property {boolean} [addSignature]
	*/

/**
	* @typedef {Object} VacationBulkCreateDto
	* @property {VacationCreateDto[]} vacations
	*/

/**
	* @typedef {Object} VacationBulkUpdateDto
	* @property {VacationUpdateDto[]} vacations
	*/

const vacationV2Service = {
	/**
		* Get all vacations for the current organization
		* @returns {Promise<VacationView[]>}
		*/
	async getAll() {
		const { data } = await axios.get("vacations-v2");

		return data;
	},

	/**
		* Get only active vacations
		* @returns {Promise<VacationView[]>}
		*/
	async getActive() {
		const { data } = await axios.get("vacations-v2/active");

		return data;
	},

	/**
		* Get current vacation if any
		* @returns {Promise<VacationView|null>}
		*/
	async getCurrent() {
		const { data } = await axios.get("vacations-v2/current-vacation");

		return data ?? null;
	},

	/**
		* Get vacation by ID
		* @param {string} vacationId
		* @returns {Promise<VacationView>}
		*/
	async getById(vacationId) {
		const { data } = await axios.get(`vacations-v2/${vacationId}`);

		return data;
	},

	/**
		* Create a new vacation
		* @param {VacationCreateDto} payload
		* @returns {Promise<VacationView>}
		*/
	async create(payload) {
		const { data } = await axios.post("vacations-v2", payload);

		return data;
	},

	/**
		* Create multiple vacations
		* @param {VacationBulkCreateDto} payload
		* @returns {Promise<VacationView[]>}
		*/
	async bulkCreate(payload) {
		const { data } = await axios.post("vacations-v2/bulk", payload);

		return data;
	},

	/**
		* Update a vacation
		* @param {string} vacationId
		* @param {VacationUpdateDto} payload
		* @returns {Promise<VacationView>}
		*/
	async update(vacationId, payload) {
		const { data } = await axios.put(`vacations-v2/${vacationId}`, payload);

		return data;
	},

	/**
		* Bulk update vacations
		* @param {VacationBulkUpdateDto} payload
		* @returns {Promise<VacationView[]>}
		*/
	async bulkUpdate(payload) {
		const { data } = await axios.put("vacations-v2/bulk", payload);

		return data;
	},

	/**
		* Delete a vacation
		* @param {string} vacationId
		* @returns {Promise<void>}
		*/
	async remove(vacationId) {
		await axios.delete(`vacations-v2/${vacationId}`);
	}
};

export default vacationV2Service;
